import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, GitBranch, Code } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SendingWindowEditor from "@/components/SendingWindowEditor";

const STEPS = [
  { id: "settings", label: "Settings" },
  { id: "sequence", label: "Sequence" },
  { id: "leads", label: "Leads" },
];

// Stable per-row id so dynamic step / variant additions don't trigger
// index-keyed remounts (which lose focus state on the inputs).
const newUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

export default function CampaignWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [inboxes, setInboxes] = useState([]);
  const [name, setName] = useState("");
  const [selectedInboxes, setSelectedInboxes] = useState([]);
  const [dailyCap, setDailyCap] = useState(100);
  const [timezone, setTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });
  const [sendingWindow, setSendingWindow] = useState({
    start_hour: 0,
    end_hour: 24,
    days: [0, 1, 2, 3, 4, 5, 6],
  });
  const [sendIntervalMinutes, setSendIntervalMinutes] = useState(0);
  const [seqSteps, setSeqSteps] = useState([
    { _uid: newUid(), step_number: 1, wait_days: 0, subject: "", body_html: "", htmlMode: false, ab_variants: [] },
  ]);
  const [csvText, setCsvText] = useState("email,first_name,last_name,company\n");
  const [campaignId, setCampaignId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/inboxes").then((r) => setInboxes(r.data));
  }, []);

  const next = async () => {
    if (step === 0) {
      if (!name.trim()) return toast.error("Name your campaign");
      if (selectedInboxes.length === 0)
        return toast.error("Select at least one inbox");
      setBusy(true);
      try {
        const body = {
          name,
          inbox_ids: selectedInboxes,
          daily_cap: dailyCap,
          timezone,
          sending_window: sendingWindow,
          send_interval_minutes: sendIntervalMinutes,
        };
        const r = campaignId
          ? await api.patch(`/campaigns/${campaignId}`, body)
          : await api.post("/campaigns", body);
        setCampaignId(r.data.id);
        setStep(1);
      } catch {
        toast.error("Failed to save campaign");
      } finally {
        setBusy(false);
      }
    } else if (step === 1) {
      if (seqSteps.some((s) => !s.subject.trim() || !s.body_html.trim()))
        return toast.error("Each step needs a subject and body");
      setBusy(true);
      try {
        await api.put(`/campaigns/${campaignId}/sequence`, {
          steps: seqSteps.map((s, i) => ({
            ...s,
            step_number: i + 1,
            ab_variants: (s.ab_variants || []).filter(
              (v) => v.subject?.trim() && v.body_html?.trim(),
            ),
          })),
        });
        setStep(2);
      } catch {
        toast.error("Failed to save sequence");
      } finally {
        setBusy(false);
      }
    } else if (step === 2) {
      setBusy(true);
      try {
        const blob = new Blob([csvText], { type: "text/csv" });
        const fd = new FormData();
        fd.append("file", blob, "leads.csv");
        const r = await api.post(`/campaigns/${campaignId}/leads/upload`, fd);
        toast.success(
          `Imported ${r.data.inserted} lead${r.data.inserted === 1 ? "" : "s"} (${r.data.skipped} skipped)`,
        );
        navigate(`/campaigns/${campaignId}`);
      } catch {
        toast.error("Failed to import leads");
      } finally {
        setBusy(false);
      }
    }
  };

  const back = () => {
    if (step === 0) navigate("/campaigns");
    else setStep(step - 1);
  };

  const addStep = () =>
    setSeqSteps([
      ...seqSteps,
      {
        _uid: newUid(),
        step_number: seqSteps.length + 1,
        wait_days: 3,
        subject: "",
        body_html: "",
        htmlMode: false,
        ab_variants: [],
      },
    ]);
  const updStep = (i, patch) => {
    const next = [...seqSteps];
    next[i] = { ...next[i], ...patch };
    setSeqSteps(next);
  };
  const rmStep = (i) => {
    if (seqSteps.length === 1) return;
    setSeqSteps(seqSteps.filter((_, idx) => idx !== i));
  };

  const insertVar = (i, v) => {
    const t = seqSteps[i].body_html + ` {{${v}}} `;
    updStep(i, { body_html: t });
  };

  const addVariant = (i) => {
    const next = [...seqSteps];
    next[i] = {
      ...next[i],
      ab_variants: [...(next[i].ab_variants || []), { _uid: newUid(), subject: "", body_html: "" }],
    };
    setSeqSteps(next);
  };
  const updVariant = (i, vi, patch) => {
    const next = [...seqSteps];
    const variants = [...(next[i].ab_variants || [])];
    variants[vi] = { ...variants[vi], ...patch };
    next[i] = { ...next[i], ab_variants: variants };
    setSeqSteps(next);
  };
  const rmVariant = (i, vi) => {
    const next = [...seqSteps];
    next[i] = {
      ...next[i],
      ab_variants: (next[i].ab_variants || []).filter((_, idx) => idx !== vi),
    };
    setSeqSteps(next);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
          New campaign
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
          {STEPS[step].label}
        </h1>
      </div>
      <Stepper step={step} />

      {step === 0 && (
        <div className="space-y-6">
          <Section title="Basics">
            <Field label="Campaign name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Q1 cold outbound - Series A founders"
                data-testid="campaign-name-input"
                className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Daily cap">
              <input
                type="number"
                min={1}
                value={dailyCap}
                onChange={(e) => setDailyCap(parseInt(e.target.value || 0))}
                data-testid="daily-cap-input"
                className="w-32 bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
              />
            </Field>
            <Field
              label="Min minutes between sends"
              hint="0 = no rate limit. Actual interval is jittered −30%/+40% to avoid pattern detection."
            >
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={sendIntervalMinutes}
                  onChange={(e) =>
                    setSendIntervalMinutes(Math.max(0, parseInt(e.target.value || 0)))
                  }
                  data-testid="send-interval-input"
                  className="w-32 bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
                <div className="flex gap-1.5">
                  {[0, 2, 5, 10, 20].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setSendIntervalMinutes(p)}
                      data-testid={`send-interval-preset-${p}`}
                      className={`text-[10px] uppercase tracking-[0.18em] font-mono px-2 py-1 rounded border transition-colors ${
                        sendIntervalMinutes === p
                          ? "bg-white text-black border-white"
                          : "bg-[#0d0d0d] border-[#27272a] text-neutral-400 hover:bg-[#161616]"
                      }`}
                    >
                      {p === 0 ? "Off" : `${p}m`}
                    </button>
                  ))}
                </div>
              </div>
            </Field>
          </Section>
          <Section title="Sending mailboxes">
            {inboxes.length === 0 ? (
              <div className="text-sm text-neutral-500">
                No inboxes connected.{" "}
                <button
                  className="underline hover:text-white"
                  onClick={() => navigate("/inboxes")}
                >
                  Connect a mailbox first
                </button>
                .
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {inboxes.map((i) => {
                  const checked = selectedInboxes.includes(i.id);
                  return (
                    <button
                      key={i.id}
                      onClick={() =>
                        setSelectedInboxes(
                          checked
                            ? selectedInboxes.filter((x) => x !== i.id)
                            : [...selectedInboxes, i.id],
                        )
                      }
                      data-testid={`inbox-pick-${i.id}`}
                      className={`flex items-center justify-between border rounded-md px-3 py-2 text-sm transition-colors ${
                        checked
                          ? "bg-white text-black border-white"
                          : "bg-[#0d0d0d] border-[#27272a] text-neutral-300 hover:bg-[#161616]"
                      }`}
                    >
                      <span className="font-mono">{i.email_address}</span>
                      {checked ? <Check className="w-4 h-4" strokeWidth={1.8} /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </Section>
          <Section title="Schedule">
            <SendingWindowEditor
              timezone={timezone}
              sendingWindow={sendingWindow}
              onChange={(patch) => {
                if (patch.timezone !== undefined) setTimezone(patch.timezone);
                if (patch.sending_window) setSendingWindow(patch.sending_window);
              }}
              testIdPrefix="wizard"
            />
          </Section>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-3">
            {seqSteps.map((s, i) => (
              <div
                key={s._uid}
                className="relative pl-6 border-l border-[#27272a]"
                data-testid={`step-${i + 1}`}
              >
                <div
                  className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-white border border-[#0a0a0a]"
                  aria-hidden
                />
                <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
                      Step {i + 1}
                    </div>
                    <div className="flex items-center gap-2">
                      {i > 0 && (
                        <div className="flex items-center gap-2 text-xs text-neutral-400">
                          <span>Wait</span>
                          <input
                            type="number"
                            min={0}
                            value={s.wait_days}
                            onChange={(e) =>
                              updStep(i, { wait_days: parseInt(e.target.value || 0) })
                            }
                            className="w-14 bg-[#0a0a0a] border border-[#27272a] rounded-md px-2 py-1 text-sm font-mono"
                          />
                          <span>days</span>
                        </div>
                      )}
                      <button
                        onClick={() => rmStep(i)}
                        className="p-1.5 text-red-400 hover:bg-[#161616] rounded-md"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <input
                    value={s.subject}
                    onChange={(e) => updStep(i, { subject: e.target.value })}
                    placeholder="Subject (variant A)"
                    data-testid={`subject-${i + 1}`}
                    className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono">
                      {s.htmlMode ? "HTML source" : "Message body"}
                    </span>
                    <button
                      type="button"
                      onClick={() => updStep(i, { htmlMode: !s.htmlMode })}
                      data-testid={`html-toggle-${i + 1}`}
                      title="Toggle raw HTML code view"
                      className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border transition-colors ${
                        s.htmlMode
                          ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
                          : "bg-[#0a0a0a] border-[#27272a] text-neutral-400 hover:bg-[#161616]"
                      }`}
                    >
                      <Code className="w-3 h-3" strokeWidth={2} /> HTML
                    </button>
                  </div>
                  <textarea
                    value={s.body_html}
                    onChange={(e) => updStep(i, { body_html: e.target.value })}
                    rows={s.htmlMode ? 9 : 7}
                    placeholder={
                      s.htmlMode
                        ? `<p>Hey {{first_name}},</p>\n<a href="{{LoomURL}}"><img src="{{LoomThumbnail}}" /></a>`
                        : `Hey {{first_name}},\n\nQuick note about {{company}}...`
                    }
                    data-testid={`body-${i + 1}`}
                    className={`w-full bg-[#0a0a0a] border rounded-md px-3 py-2 text-sm font-mono leading-relaxed ${
                      s.htmlMode ? "border-violet-500/40" : "border-[#27272a]"
                    }`}
                  />
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono mr-1">
                      Insert
                    </span>
                    {["first_name", "last_name", "company", "email"].map((v) => (
                      <button
                        key={v}
                        onClick={() => insertVar(i, v)}
                        data-testid={`insert-var-${v}-${i + 1}`}
                        className="var-chip hover:bg-white hover:text-black"
                      >
                        {`{{${v}}}`}
                      </button>
                    ))}
                    {s.htmlMode &&
                      ["LoomURL", "LoomThumbnail"].map((v) => (
                        <button
                          key={v}
                          onClick={() => insertVar(i, v)}
                          data-testid={`insert-var-${v}-${i + 1}`}
                          className="var-chip border border-violet-500/40 text-violet-200 hover:bg-violet-500 hover:text-white"
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                  </div>

                  {/* A/B variants */}
                  {(s.ab_variants || []).map((v, vi) => (
                    <div
                      key={v._uid || `${s._uid}-v${vi}`}
                      className="border border-[#1f1f1f] rounded-md p-3 space-y-2 bg-[#0a0a0a]"
                      data-testid={`variant-${i + 1}-${vi + 1}`}
                    >
                      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400/80">
                        <span className="inline-flex items-center gap-1.5">
                          <GitBranch className="w-3 h-3" strokeWidth={1.8} />
                          Variant {String.fromCharCode(66 + vi)}
                        </span>
                        <button
                          onClick={() => rmVariant(i, vi)}
                          className="text-neutral-500 hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <input
                        value={v.subject}
                        onChange={(e) =>
                          updVariant(i, vi, { subject: e.target.value })
                        }
                        placeholder={`Subject (variant ${String.fromCharCode(66 + vi)})`}
                        className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm"
                      />
                      <textarea
                        value={v.body_html}
                        onChange={(e) =>
                          updVariant(i, vi, { body_html: e.target.value })
                        }
                        rows={5}
                        placeholder="Variant body…"
                        className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => addVariant(i)}
                    data-testid={`add-variant-${i + 1}`}
                    className="text-xs font-mono uppercase tracking-[0.18em] text-amber-400/80 hover:text-amber-300 inline-flex items-center gap-1.5"
                  >
                    <GitBranch className="w-3 h-3" strokeWidth={1.8} /> Add A/B variant
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={addStep}
            data-testid="add-step-button"
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#27272a] rounded-md text-sm text-neutral-400 hover:bg-[#0d0d0d]"
          >
            <Plus className="w-4 h-4" strokeWidth={1.8} /> Add step
          </button>
        </div>
      )}

      {step === 2 && (
        <Section title="Paste CSV (or edit headers below)">
          <p className="text-sm text-neutral-400 mb-3">
            Required column: <span className="font-mono">email</span>. Optional:{" "}
            <span className="font-mono">first_name</span>,{" "}
            <span className="font-mono">last_name</span>,{" "}
            <span className="font-mono">company</span>. Extra columns are saved
            as custom fields and available via{" "}
            <span className="font-mono">{`{{column_name}}`}</span> in your sequence.
          </p>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={14}
            data-testid="csv-textarea"
            className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-xs font-mono"
            placeholder="email,first_name,last_name,company"
          />
        </Section>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={back}
          className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1.5"
          data-testid="wizard-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>
        <button
          onClick={next}
          disabled={busy}
          data-testid="wizard-next"
          className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-neutral-200 transition-colors disabled:opacity-50"
        >
          {step === 2 ? "Import & finish" : "Continue"}{" "}
          <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-mono ${
                active
                  ? "bg-white text-black border-white"
                  : done
                  ? "bg-[#161616] text-emerald-400 border-emerald-400/30"
                  : "bg-transparent text-neutral-500 border-[#27272a]"
              }`}
            >
              {done ? <Check className="w-3 h-3" strokeWidth={2} /> : i + 1}
            </div>
            <div
              className={`text-xs font-mono uppercase tracking-[0.2em] ${
                active ? "text-white" : "text-neutral-500"
              }`}
            >
              {s.label}
            </div>
            {i < STEPS.length - 1 && (
              <div className="w-12 h-px bg-[#27272a] mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
        {title}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
      <div className="text-sm text-neutral-400 sm:w-40 pt-2">{label}</div>
      <div className="flex-1">
        {children}
        {hint ? (
          <div className="text-xs text-neutral-500 mt-2 leading-relaxed">{hint}</div>
        ) : null}
      </div>
    </div>
  );
}
